/**
 * Archetype parameter templates — the defaults a new site starts from.
 *
 * These are REALISTIC, not neutral. A cellsite defaults to a 42m self-support
 * tower at 110mph wind with 240min autonomy on a -48V plant, and deliberately
 * fails some checks (redundantPathways: false) so a new site starts with a
 * plausible, non-perfect posture. A template that scored 100% would teach the
 * user nothing.
 *
 * Ported verbatim from the prototype's siteTemplate(), except `cablingStd`
 * which is now a four-value union (defect fix D2).
 */
import type { ArchetypeId, ArchetypeMeta, SiteParams } from './types.js';

/** Parameters minus the identity fields a caller must supply. */
export type SiteTemplate = Omit<SiteParams, 'archetype' | 'region'>;

/**
 * The plant a site does NOT have, stated once.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A SHARED BASE HERE AND NOWHERE ELSE IN THIS FILE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Every template above section 9 is written out in full, deliberately: those numbers are
 * a considered claim about what a plant of that kind typically looks like, and a spread
 * would hide which archetype chose what.
 *
 * Sections 9-14 are the opposite. Twenty-five fields describe equipment classes that most
 * archetypes simply do not contain — no classified area, no PV array, no storage bank, no
 * EVSE, no traction bus — and repeating twenty-five zeros across thirteen literals would
 * make the two or three values that MATTER per archetype impossible to see. Spreading this
 * and overriding what is real states the interesting part and nothing else.
 *
 * `gridInterconnect: true` is the one non-zero default, and it is truthful rather than
 * convenient: every archetype here is mains-fed. IEEE 1547 only grades the archetypes that
 * can export, so the value is inert elsewhere — but a `false` default would have asserted
 * that a data hall runs islanded.
 *
 * `transferTimeS: 0` means NOT MEASURED, not "instantaneous". The checks that read it
 * treat 0 as an undeclared value and say so, because a zero-second transfer is physically
 * impossible for a rotating machine and a check that passed on it would be scoring a gap
 * as a strength.
 */
const ABSENT = {
  /* 9 · life safety */
  transferTimeS: 0,
  branchSeparation: false,
  isolatedPowerSystem: false,
  selectiveCoordination: false,
  /* 10 · process */
  arcFlashStudy: false,
  motorLoadKw: 0,
  /* IP20 — basic indoor enclosure. The floor for an indoor assembly under IEC 61439-1. */
  mccIpRating: 20,
  /* 11 · hazardous */
  hazardZone: 'none',
  exEquipment: false,
  purgePressurization: false,
  /* 12 · generation */
  pvArrayKw: 0,
  dcArcFaultProtection: false,
  windTurbineKw: 0,
  gridInterconnect: true,
  antiIslanding: false,
  /* 13 · storage */
  bessKwh: 0,
  bessChemistry: 'none',
  thermalRunawayDetect: false,
  bessSeparationM: 0,
  /* 14 · mobility */
  evChargerKw: 0,
  evConnectorStd: 'none',
  evRcdTypeB: false,
  loadManagement: false,
  tractionVoltage: 0,
  strayCurrentControl: false,
} as const satisfies Pick<
  SiteTemplate,
  | 'transferTimeS' | 'branchSeparation' | 'isolatedPowerSystem' | 'selectiveCoordination'
  | 'arcFlashStudy' | 'motorLoadKw' | 'mccIpRating'
  | 'hazardZone' | 'exEquipment' | 'purgePressurization'
  | 'pvArrayKw' | 'dcArcFaultProtection' | 'windTurbineKw' | 'gridInterconnect' | 'antiIslanding'
  | 'bessKwh' | 'bessChemistry' | 'thermalRunawayDetect' | 'bessSeparationM'
  | 'evChargerKw' | 'evConnectorStd' | 'evRcdTypeB' | 'loadManagement'
  | 'tractionVoltage' | 'strayCurrentControl'
>;

const TEMPLATES: Record<ArchetypeId, SiteTemplate> = {
  datacenter: {
    tierTarget: 4, acVoltage: 400, frequency: 50, redundancy: '2N',
    generator: true, ups: true, concurrentMaint: true, faultTolerant: true,
    dc48: false, dcMeasMin: -48, dcMeasMax: -54, hvdc400: true,
    itLoadKw: 480, totalKw: 760, pue: 1.45,
    battAh: 600, battV: 480, autonomyMin: 12, reserveSoc: 20,
    towerType: 'none', towerHeightM: 0, windSpeedMph: 0, iceIn: 0,
    structureClass: 'none', seismicZone: 2,
    tempMinC: 18, tempMaxC: 27, nebs: false, fireRating: true,
    groundRing: true, tmgb: true, spg: true, earthOhms: 3, spd: true, lpl: 'II',
    cablingStd: 'tia568', pathwaySep: true, redundantPathways: true, trPresent: true,
    poeType: 0, poeCableM: 0, fmpsClass4: false, packetV: 0,
    remotePower: false, oamMonitoring: true, alarmToNoc: true,
    ...ABSENT,
    /* A 2N data hall transfers on a paralleled genset bus; 10 s is the NFPA 110
       Type 10 figure and what a commissioned load test on this plant reports. */
    transferTimeS: 10, selectiveCoordination: true, arcFlashStudy: true, mccIpRating: 42,
  },

  cellsite: {
    tierTarget: 2, acVoltage: 230, frequency: 50, redundancy: 'N+1',
    generator: true, ups: false, concurrentMaint: false, faultTolerant: false,
    dc48: true, dcMeasMin: -43.5, dcMeasMax: -56.2, hvdc400: false,
    itLoadKw: 6, totalKw: 9, pue: 1.7,
    battAh: 700, battV: 51.2, autonomyMin: 240, reserveSoc: 20,
    towerType: 'self-support', towerHeightM: 42, windSpeedMph: 110, iceIn: 0.5,
    structureClass: 'II', seismicZone: 3,
    tempMinC: -10, tempMaxC: 50, nebs: true, fireRating: true,
    groundRing: true, tmgb: true, spg: true, earthOhms: 4, spd: true, lpl: 'II',
    cablingStd: 'tia568', pathwaySep: true, redundantPathways: false, trPresent: false,
    poeType: 0, poeCableM: 0, fmpsClass4: false, packetV: 0,
    remotePower: true, oamMonitoring: true, alarmToNoc: true,
    ...ABSENT,
    /* An unattended cabinet genset with no attendant: slower to accept load, and
       nobody has done an incident-energy study on a 9 kW site. */
    transferTimeS: 15, mccIpRating: 54,
  },

  centraloffice: {
    tierTarget: 3, acVoltage: 400, frequency: 50, redundancy: '2N',
    generator: true, ups: true, concurrentMaint: true, faultTolerant: false,
    dc48: true, dcMeasMin: -44, dcMeasMax: -55, hvdc400: false,
    itLoadKw: 120, totalKw: 210, pue: 1.65,
    battAh: 2000, battV: 48, autonomyMin: 300, reserveSoc: 25,
    towerType: 'none', towerHeightM: 0, windSpeedMph: 0, iceIn: 0,
    structureClass: 'none', seismicZone: 2,
    tempMinC: -5, tempMaxC: 45, nebs: true, fireRating: true,
    groundRing: true, tmgb: true, spg: true, earthOhms: 6, spd: true, lpl: 'III',
    cablingStd: 'tia568', pathwaySep: true, redundantPathways: true, trPresent: true,
    poeType: 3, poeCableM: 75, fmpsClass4: false, packetV: 0,
    remotePower: false, oamMonitoring: true, alarmToNoc: true,
    ...ABSENT,
    transferTimeS: 10, selectiveCoordination: true, arcFlashStudy: true, mccIpRating: 42,
  },

  edge5g: {
    tierTarget: 2, acVoltage: 230, frequency: 60, redundancy: 'N',
    generator: false, ups: true, concurrentMaint: false, faultTolerant: false,
    dc48: true, dcMeasMin: -46, dcMeasMax: -55, hvdc400: false,
    itLoadKw: 18, totalKw: 26, pue: 1.8,
    battAh: 300, battV: 48, autonomyMin: 90, reserveSoc: 15,
    towerType: 'monopole', towerHeightM: 28, windSpeedMph: 115, iceIn: 0.25,
    structureClass: 'III', seismicZone: 4,
    tempMinC: -20, tempMaxC: 50, nebs: true, fireRating: false,
    groundRing: true, tmgb: false, spg: true, earthOhms: 8, spd: true, lpl: 'III',
    cablingStd: 'tia568', pathwaySep: true, redundantPathways: false, trPresent: false,
    poeType: 4, poeCableM: 95, fmpsClass4: true, packetV: 336,
    remotePower: true, oamMonitoring: true, alarmToNoc: false,
    ...ABSENT,
    /* No generator at all, so there is no transfer to time. */
    mccIpRating: 54,
  },

  fmps: {
    tierTarget: 1, acVoltage: 400, frequency: 60, redundancy: 'N+1',
    generator: false, ups: true, concurrentMaint: false, faultTolerant: false,
    dc48: false, dcMeasMin: -48, dcMeasMax: -54, hvdc400: true,
    itLoadKw: 14, totalKw: 20, pue: 1.5,
    battAh: 200, battV: 48, autonomyMin: 60, reserveSoc: 15,
    towerType: 'none', towerHeightM: 0, windSpeedMph: 0, iceIn: 0,
    structureClass: 'none', seismicZone: 2,
    tempMinC: 0, tempMaxC: 40, nebs: false, fireRating: true,
    groundRing: true, tmgb: true, spg: true, earthOhms: 5, spd: true, lpl: 'III',
    cablingStd: 'tia568', pathwaySep: true, redundantPathways: false, trPresent: true,
    poeType: 4, poeCableM: 90, fmpsClass4: true, packetV: 430,
    remotePower: true, oamMonitoring: true, alarmToNoc: true,
    ...ABSENT,
    mccIpRating: 42,
  },


  /* ======================================================================== *
   * Beyond telecom and data centres.
   *
   * Written to the same rule as the five above: REALISTIC, NOT NEUTRAL, and
   * deliberately imperfect. `industrial` starts with no arc-flash study and no
   * selective coordination because that is what a real process plant looks like
   * before anybody has done the work, and a template that scored 100% would teach
   * an operator nothing about their own site.
   * ======================================================================== */

  /**
   * A hospital or clinic. Graded as a Category 1 essential electrical system.
   *
   * `itLoadKw` is imaging plus IT rather than the whole building — the distinction
   * matters because `pue` is derived from the pair, and a hospital's total load is
   * dominated by HVAC and lifts that no IT-efficiency figure describes.
   */
  healthcare: {
    tierTarget: 3, acVoltage: 400, frequency: 50, redundancy: '2N',
    generator: true, ups: true, concurrentMaint: true, faultTolerant: false,
    dc48: false, dcMeasMin: -48, dcMeasMax: -54, hvdc400: false,
    itLoadKw: 220, totalKw: 1400, pue: 1.6,
    battAh: 400, battV: 480, autonomyMin: 15, reserveSoc: 25,
    towerType: 'none', towerHeightM: 0, windSpeedMph: 0, iceIn: 0,
    structureClass: 'none', seismicZone: 3,
    tempMinC: 20, tempMaxC: 26, nebs: false, fireRating: true,
    groundRing: true, tmgb: true, spg: true, earthOhms: 2, spd: true, lpl: 'II',
    cablingStd: 'tia568', pathwaySep: true, redundantPathways: true, trPresent: true,
    poeType: 3, poeCableM: 80, fmpsClass4: false, packetV: 0,
    remotePower: false, oamMonitoring: true, alarmToNoc: true,
    ...ABSENT,
    transferTimeS: 10, branchSeparation: true,
    /* Commonly absent even in well-run hospitals, and the check should say so rather
       than the template pretending otherwise. */
    isolatedPowerSystem: false,
    selectiveCoordination: true, arcFlashStudy: true, mccIpRating: 42,
  },

  /**
   * A process plant, manufacturing line or water/wastewater works.
   *
   * The load is motors, so `motorLoadKw` carries the largest one and `totalKw` dwarfs
   * `itLoadKw` — the control room and SCADA are a rounding error next to a 315 kW pump.
   */
  industrial: {
    tierTarget: 2, acVoltage: 400, frequency: 50, redundancy: 'N+1',
    generator: true, ups: true, concurrentMaint: false, faultTolerant: false,
    dc48: false, dcMeasMin: -48, dcMeasMax: -54, hvdc400: false,
    itLoadKw: 40, totalKw: 2200, pue: 1.9,
    /* A 110 V DC control battery for switchgear tripping, not an IT plant. */
    battAh: 200, battV: 110, autonomyMin: 30, reserveSoc: 20,
    towerType: 'none', towerHeightM: 0, windSpeedMph: 0, iceIn: 0,
    structureClass: 'none', seismicZone: 2,
    tempMinC: -5, tempMaxC: 45, nebs: false, fireRating: true,
    groundRing: true, tmgb: false, spg: false, earthOhms: 4, spd: true, lpl: 'III',
    cablingStd: 'legacy', pathwaySep: true, redundantPathways: false, trPresent: false,
    poeType: 0, poeCableM: 0, fmpsClass4: false, packetV: 0,
    remotePower: false, oamMonitoring: true, alarmToNoc: false,
    ...ABSENT,
    transferTimeS: 15,
    /* The two gaps a process plant most often has, and the two that hurt a person
       rather than a service level. */
    selectiveCoordination: false, arcFlashStudy: false,
    motorLoadKw: 315, mccIpRating: 54,
  },

  /**
   * An oil, gas or petrochemical installation with a classified area.
   *
   * `lpl: 'I'` is not a stylistic choice: IEC 62305-2's risk assessment puts a
   * structure containing a hydrocarbon atmosphere in the highest protection level,
   * because the consequence of a strike is not equipment damage.
   */
  hazardous: {
    tierTarget: 3, acVoltage: 400, frequency: 50, redundancy: '2N',
    generator: true, ups: true, concurrentMaint: true, faultTolerant: false,
    dc48: false, dcMeasMin: -48, dcMeasMax: -54, hvdc400: false,
    itLoadKw: 25, totalKw: 1800, pue: 1.9,
    battAh: 300, battV: 110, autonomyMin: 60, reserveSoc: 25,
    towerType: 'none', towerHeightM: 0, windSpeedMph: 0, iceIn: 0,
    structureClass: 'none', seismicZone: 2,
    tempMinC: 0, tempMaxC: 55, nebs: false, fireRating: true,
    groundRing: true, tmgb: false, spg: true, earthOhms: 1, spd: true, lpl: 'I',
    cablingStd: 'legacy', pathwaySep: true, redundantPathways: true, trPresent: false,
    poeType: 0, poeCableM: 0, fmpsClass4: false, packetV: 0,
    remotePower: false, oamMonitoring: true, alarmToNoc: true,
    ...ABSENT,
    transferTimeS: 10, selectiveCoordination: true, arcFlashStudy: true,
    motorLoadKw: 500, mccIpRating: 65,
    hazardZone: 'zone1', exEquipment: true, purgePressurization: false,
  },

  /**
   * A rail or transit traction substation with its auxiliary and signalling supplies.
   *
   * `tractionVoltage` is what makes this archetype itself — a DC traction bus returning
   * current through the running rails is the condition EN 50122 exists for, and no other
   * archetype has one.
   */
  transport: {
    tierTarget: 3, acVoltage: 400, frequency: 50, redundancy: '2N',
    generator: false, ups: true, concurrentMaint: true, faultTolerant: false,
    /* -48 V for signalling and telecoms, alongside the traction bus. */
    dc48: true, dcMeasMin: -44, dcMeasMax: -56, hvdc400: false,
    itLoadKw: 30, totalKw: 3500, pue: 1.8,
    battAh: 500, battV: 110, autonomyMin: 120, reserveSoc: 30,
    towerType: 'none', towerHeightM: 0, windSpeedMph: 0, iceIn: 0,
    structureClass: 'none', seismicZone: 3,
    tempMinC: -15, tempMaxC: 45, nebs: false, fireRating: true,
    groundRing: true, tmgb: false, spg: false, earthOhms: 1, spd: true, lpl: 'II',
    cablingStd: 'legacy', pathwaySep: true, redundantPathways: true, trPresent: false,
    poeType: 0, poeCableM: 0, fmpsClass4: false, packetV: 0,
    remotePower: true, oamMonitoring: true, alarmToNoc: true,
    ...ABSENT,
    selectiveCoordination: true, arcFlashStudy: true, mccIpRating: 54,
    tractionVoltage: 750, strayCurrentControl: true,
  },

  /**
   * A generating plant. HYBRID BY DEFAULT — a PV array and two turbines.
   *
   * One archetype rather than two because the governing interconnection document
   * (IEEE 1547) is identical, and the parts that genuinely differ are gated on the
   * capacity parameters: IEC 62548's DC-side clauses fire on `pvArrayKw`, IEC 61400-24's
   * lightning clauses on `windTurbineKw`. A site with both is graded against both.
   *
   * Hybrid rather than PV-only for a reason that is about the DEFAULT rather than about
   * co-location being common (which it increasingly is). A template with `windTurbineKw:
   * 0` makes every IEC 61400-24 check N/A, so a new renewable site would silently start
   * with an entire standard hidden — and `threshold-scale.test.ts` caught it, because a
   * declared threshold scale that is never decisive on any template is a scale nothing
   * verifies. Zeroing the technology a site does not have is one edit; discovering a
   * standard was never being graded is not.
   *
   * `itLoadKw` and `totalKw` are AUXILIARY load — the tracker motors, the control room,
   * the inverter cooling. Generation capacity is not a load and does not belong there.
   */
  renewable: {
    tierTarget: 2, acVoltage: 400, frequency: 50, redundancy: 'N',
    generator: false, ups: true, concurrentMaint: false, faultTolerant: false,
    dc48: false, dcMeasMin: -48, dcMeasMax: -54, hvdc400: false,
    itLoadKw: 5, totalKw: 60, pue: 1.4,
    battAh: 200, battV: 48, autonomyMin: 30, reserveSoc: 20,
    /* Array racking carries wind and ice load even with no tower. */
    towerType: 'none', towerHeightM: 0, windSpeedMph: 100, iceIn: 0.25,
    structureClass: 'none', seismicZone: 3,
    tempMinC: -10, tempMaxC: 50, nebs: false, fireRating: false,
    groundRing: true, tmgb: false, spg: false, earthOhms: 5, spd: true, lpl: 'III',
    cablingStd: 'legacy', pathwaySep: false, redundantPathways: false, trPresent: false,
    poeType: 0, poeCableM: 0, fmpsClass4: false, packetV: 0,
    remotePower: false, oamMonitoring: true, alarmToNoc: true,
    ...ABSENT,
    arcFlashStudy: false, mccIpRating: 54,
    pvArrayKw: 2400, dcArcFaultProtection: true,
    /* Two 1.8 MW machines. `lpl: 'III'` above is below the LPL I that IEC 61400-24
       starts from, so the turbine check warns — a deliberate imperfection, and the
       remedy is either LPL I or the risk assessment that justifies less. */
    windTurbineKw: 3600,
    gridInterconnect: true, antiIslanding: true,
  },

  /**
   * A battery energy storage installation, or a microgrid built around one.
   *
   * `battAh`/`battV` and `bessKwh` are DIFFERENT THINGS and both are populated. The
   * first is the station's own 48 V control battery; the second is the storage asset
   * itself. Collapsing them would make NFPA 855's thresholds read against a control
   * battery, and a 100 Ah tripping battery is not a 4 MWh installation.
   */
  storage: {
    tierTarget: 3, acVoltage: 400, frequency: 50, redundancy: 'N+1',
    generator: true, ups: true, concurrentMaint: true, faultTolerant: false,
    dc48: false, dcMeasMin: -48, dcMeasMax: -54, hvdc400: false,
    itLoadKw: 8, totalKw: 120, pue: 1.3,
    battAh: 100, battV: 48, autonomyMin: 240, reserveSoc: 20,
    towerType: 'none', towerHeightM: 0, windSpeedMph: 0, iceIn: 0,
    structureClass: 'none', seismicZone: 3,
    tempMinC: -20, tempMaxC: 45, nebs: false, fireRating: true,
    groundRing: true, tmgb: false, spg: true, earthOhms: 4, spd: true, lpl: 'II',
    cablingStd: 'legacy', pathwaySep: true, redundantPathways: false, trPresent: false,
    poeType: 0, poeCableM: 0, fmpsClass4: false, packetV: 0,
    remotePower: false, oamMonitoring: true, alarmToNoc: true,
    ...ABSENT,
    selectiveCoordination: true, arcFlashStudy: true, mccIpRating: 54,
    gridInterconnect: true, antiIslanding: true,
    bessKwh: 4000, bessChemistry: 'lfp', thermalRunawayDetect: true,
    /* 0.9 m — the 3 ft NFPA 855 Table 4.1 asks between units. */
    bessSeparationM: 0.9,
  },

  /**
   * A commercial building, retail store or bank branch.
   *
   * The least redundant template in the catalogue, on purpose. A branch has a comms
   * closet UPS and nothing else, and the emergency-power clauses that actually govern it
   * (NEC 700) are about egress lighting rather than about load continuity.
   */
  commercial: {
    tierTarget: 1, acVoltage: 400, frequency: 50, redundancy: 'N',
    generator: false, ups: true, concurrentMaint: false, faultTolerant: false,
    dc48: false, dcMeasMin: -48, dcMeasMax: -54, hvdc400: false,
    itLoadKw: 12, totalKw: 85, pue: 1.9,
    battAh: 100, battV: 48, autonomyMin: 20, reserveSoc: 15,
    towerType: 'none', towerHeightM: 0, windSpeedMph: 0, iceIn: 0,
    structureClass: 'none', seismicZone: 2,
    tempMinC: 5, tempMaxC: 35, nebs: false, fireRating: true,
    groundRing: false, tmgb: false, spg: false, earthOhms: 10, spd: true, lpl: 'IV',
    cablingStd: 'tia568', pathwaySep: false, redundantPathways: false, trPresent: true,
    poeType: 3, poeCableM: 70, fmpsClass4: false, packetV: 0,
    remotePower: false, oamMonitoring: false, alarmToNoc: false,
    ...ABSENT,
    /* No generator, so there is no transfer to time — 0 reads as undeclared and the
       NFPA 110 check says exactly that rather than passing on a zero. */
    mccIpRating: 30,
  },

  /**
   * An EV charging hub.
   *
   * `totalKw` is nearly all EVSE, which is why `pue` sits at 1.1 — there is almost no
   * overhead to divide by. It is the one archetype where the efficiency figure the
   * platform inherited from data centres happens to be nearly meaningless, and it is
   * recorded honestly rather than left blank.
   */
  evcharging: {
    tierTarget: 1, acVoltage: 400, frequency: 50, redundancy: 'N',
    generator: false, ups: false, concurrentMaint: false, faultTolerant: false,
    dc48: false, dcMeasMin: -48, dcMeasMax: -54, hvdc400: false,
    itLoadKw: 3, totalKw: 720, pue: 1.1,
    battAh: 0, battV: 48, autonomyMin: 0, reserveSoc: 0,
    towerType: 'none', towerHeightM: 0, windSpeedMph: 0, iceIn: 0,
    structureClass: 'none', seismicZone: 2,
    tempMinC: -10, tempMaxC: 45, nebs: false, fireRating: false,
    groundRing: true, tmgb: false, spg: false, earthOhms: 5, spd: true, lpl: 'III',
    cablingStd: 'legacy', pathwaySep: true, redundantPathways: false, trPresent: false,
    poeType: 0, poeCableM: 0, fmpsClass4: false, packetV: 0,
    remotePower: false, oamMonitoring: true, alarmToNoc: true,
    ...ABSENT,
    arcFlashStudy: false, mccIpRating: 54, gridInterconnect: true,
    evChargerKw: 720, evConnectorStd: 'ccs', evRcdTypeB: true, loadManagement: true,
  },
};

/** Deep copy, so callers cannot mutate the shared template. */
export function siteTemplate(archetype: ArchetypeId): SiteTemplate {
  return structuredClone(TEMPLATES[archetype]);
}

export const ARCHETYPES: readonly ArchetypeMeta[] = [
  {
    id: 'datacenter', label: 'Data Center', short: 'DC', icon: 'ic-server',
    desc: 'Uptime/TIA-942 rated facility, AC distribution, UPS + generator.',
  },
  {
    id: 'cellsite', label: 'Telecom Cell Site', short: 'BTS', icon: 'ic-tower',
    desc: '-48V DC plant on an antenna-supporting structure.',
  },
  {
    id: 'centraloffice', label: 'Central Office / MSC', short: 'CO', icon: 'ic-building',
    desc: 'Carrier exchange — large -48V DC plant + AC plant.',
  },
  {
    id: 'edge5g', label: 'Edge / 5G Node', short: 'EDGE', icon: 'ic-route',
    desc: 'Distributed micro-site, hybrid DC + remote power.',
  },
  {
    id: 'fmps', label: 'FMPS / PoE Site', short: 'FMPS', icon: 'ic-plug',
    desc: 'Fault-managed (Class 4) & PoE-powered distributed loads.',
  },
  {
    id: 'healthcare', label: 'Healthcare Facility', short: 'MED', icon: 'ic-shield',
    desc: 'Category 1 essential electrical system — life-safety, critical and equipment branches.',
  },
  {
    id: 'industrial', label: 'Industrial / Process Plant', short: 'IND', icon: 'ic-server',
    desc: 'Motor-dominated load, MCC and switchgear, arc-flash and machinery safety.',
  },
  {
    id: 'hazardous', label: 'Hazardous Area Installation', short: 'EX', icon: 'ic-alert',
    desc: 'Classified explosive atmosphere — Ex equipment selection and installation.',
  },
  {
    id: 'transport', label: 'Rail & Transit Traction', short: 'RAIL', icon: 'ic-route',
    desc: 'DC traction supply with rail return, plus signalling and auxiliary power.',
  },
  {
    id: 'renewable', label: 'Renewable Generation', short: 'GEN', icon: 'ic-zap',
    desc: 'PV array or wind plant with a utility interconnection.',
  },
  {
    id: 'storage', label: 'Energy Storage / Microgrid', short: 'BESS', icon: 'ic-battery',
    desc: 'Stationary electrochemical storage, thermal-runaway risk and islanded operation.',
  },
  {
    id: 'commercial', label: 'Commercial Building / Branch', short: 'COM', icon: 'ic-building',
    desc: 'Retail, office or branch premises — emergency lighting and egress power.',
  },
  {
    id: 'evcharging', label: 'EV Charging Hub', short: 'EVSE', icon: 'ic-plug',
    desc: 'Conductive EV supply equipment, DC fault protection and load management.',
  },
];
